import model from "../model";
import type { Game, GameInfoValue } from "../types/game";
import { compareGameIDs, removeArrayItem, unwrapGameID } from "@/ts/utils/utils";
import { getGamesByIDs, getGamesByIDsOrGroups } from "./games-service";
import { watch } from "vue";
import { pageReloadComplete } from "./casino/casino-service";

type LobbyHelperWorkflow = {
    id: number,
    featured: LobbyHelperWorkflowItem[],
    list: LobbyHelperWorkflowItem[],
};

type LobbyHelperWorkflowItem = {
    gameId?: GameInfoValue,
    gameGroup?: GameInfoValue,
};

type LobbyHelperResonse = LobbyHelperWorkflow[] | null;

let cachedData: LobbyHelperResonse = null;
let cachedDataPromise: Promise<LobbyHelperResonse> | null = null;

export default {
    init(){
        watch(() => model.userLoggedIn, () => {
            cachedData = null;
            cachedDataPromise = null;
        }, {immediate: true});
    },
}

export async function getLobbyHelperWorkflowById(workflowId: number): Promise<LobbyHelperWorkflow | null>{
    let data = await getData();
    if(!data) return null;

    return data.find(x => x.id === workflowId) || null;
}

export async function populateGameDataFromLobbyHelperData(games: Game[], lobbyHelperData: LobbyHelperWorkflow): Promise<Game[]>{
    let listedGames: Game[] = [];

    let featuredGames = await populateData(lobbyHelperData.featured);
    listedGames.forEach(item => item.featured = true);

    await populateData(lobbyHelperData.list, featuredGames);

    for(let i = games.length - 1; i >= 0; i--){
        const game = games[i];
        if(!game) continue;

        if(listedGames.some(x => compareGameIDs(x.gameId, game.gameId))){
            removeArrayItem(game, games);
        }
    }

    games = listedGames.concat(games || []);
    
    async function populateData(lobbyHelperItems: LobbyHelperWorkflowItem[], gamesToIgnore?: Game[]){
        let existingGames: {
            position: number,
            workflowItem?: LobbyHelperWorkflowItem, 
            game?: Game,
        }[] = [];

        let extraGames: {
            position: number,
            workflowItem?: LobbyHelperWorkflowItem, 
            game?: Game,
        }[] = [];

        for(let i = 0, len = lobbyHelperItems.length; i < len; i++){
            let item = lobbyHelperItems[i];
            if(!item) continue;

            if(item.gameId){
                let found = false;
    
                for(let gameIndex = games.length - 1; gameIndex >= 0; gameIndex--){
                    let game = games[gameIndex];
                    if(game && compareGameIDs(item.gameId, game.gameId)){
                        existingGames.push({position: i, game: game});
                        removeArrayItem(game, games);
                        found = true;
                        continue;
                    }
                }
    
                if(!found) extraGames.push({position: i, workflowItem: {gameId: item.gameId}});
            }

            if(item.gameGroup){
                extraGames.push({position: i, workflowItem: {gameGroup: item.gameGroup}});
            }
        }

        let extraGameIDs: GameInfoValue[] = [];
        for(let key in extraGames){
            let val = extraGames[key];
            if(val?.workflowItem?.gameId) extraGameIDs.push(val.workflowItem.gameId);
            if(val?.workflowItem?.gameGroup) extraGameIDs.push(val.workflowItem.gameGroup);
        }

        if(extraGameIDs.length > 0){
            const gameResp = await getGamesByIDsOrGroups(extraGameIDs, gamesToIgnore?.map(x => unwrapGameID(x.gameId)));
            
            gameResp?.data?.games?.forEach?.(game => {
                let item = extraGames.find(x => !x.game && (x.workflowItem?.gameId && compareGameIDs(x.workflowItem.gameId, game.gameId) || x.workflowItem?.gameGroup == game.gameGroup));
                if(!item) return;
                item.game = game;
            });

            existingGames = existingGames.concat(extraGames);
        }

        existingGames.sort((i1, i2) => i1.position - i2.position);

        let finalGames: Game[] = [];
        existingGames.forEach(item => item.game && finalGames.push(item.game));

        listedGames = listedGames.concat(finalGames);
        return finalGames;
    }

    return games;
}

export async function getLobbyHelperData(){
    return await getData();
}

function getData(): Promise<LobbyHelperResonse> {
    if(cachedData) return Promise.resolve(cachedData);
    if(cachedDataPromise) return cachedDataPromise;

    const staticUrl = model.clientConfig.games?.lobbyHelper;
    if(!staticUrl) return Promise.resolve(null);

    cachedDataPromise = new Promise(resolve => {
        onRetry();
        async function onRetry(){
            let url = staticUrl;
            await pageReloadComplete();
            if(!url) {
                resolve(null);
                return;
            }
            try{
                url = url.replace('{userId}', model.userId || '0') + '?request=' + JSON.stringify({siteId: model.siteId});
                let resp = await fetch(url);
                if(!resp.ok) throw new Error('Lobby Helper response error');
                let data = await resp.json() as LobbyHelperResonse;

                data?.forEach(item => {
                    model.lobbyHelperDataExists[item.id] = item.list.length > 0 || item.featured.length > 0;
                });

                cachedData = data;
                cachedDataPromise = null;
                resolve(cachedData);
            }
            catch(e){
                console.warn("Failed to contact LobbyHelper, retrying in 3 seconds...");
                setTimeout(onRetry, 3000);
            }
        }
    });

    return cachedDataPromise;
}